import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Plus, Trash2, Edit2, RefreshCw, ChevronLeft, ChevronRight, Activity } from 'lucide-react'
import {
  listDNSRecords,
  createDNSRecord,
  updateDNSRecord,
  deleteDNSRecord,
  resolveDNS,
  type DNSRecordCreate,
  type DNSRecordUpdate,
  type DNSResolveResponse,
} from '@/lib/api'
import { queryClient } from '@/lib/query-client'
import { useMe } from '@/lib/use-me'
import { AppShell } from '@/components/layout/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'SRV', 'NS', 'PTR', 'CAA']

// Both hosted zones are public Route53 zones; they differ by what they front.
// The backend keys stay 'public'/'private' — these are display labels only.
const ZONE_LABELS: Record<'public' | 'private', string> = {
  public: 'Public Zone for Public NLB',
  private: 'Public Zone for Private NLB & Private Link EP',
}

// Show a fixed page of records at a time so the list stays a predictable size
// even for zones with hundreds of records.
const PAGE_SIZE = 20

export default function DNSManagement() {
  const { cloudCustomerId, isAdmin } = useMe()
  const [zone, setZone] = useState<'public' | 'private'>('public')
  const [filterType, setFilterType] = useState<string>('')
  const [page, setPage] = useState(0)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [resolveResult, setResolveResult] = useState<DNSResolveResponse | null>(null)
  const [resolvingRecord, setResolvingRecord] = useState<string | null>(null)
  const [useMultipleIPs, setUseMultipleIPs] = useState(false)
  const [ipValues, setIpValues] = useState<string[]>([''])
  const [editUseMultipleIPs, setEditUseMultipleIPs] = useState(false)
  const [editIpValues, setEditIpValues] = useState<string[]>([''])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dns-records', zone, filterType],
    queryFn: () => listDNSRecords(zone, filterType || undefined),
  })

  // A new zone/filter is a fresh result set — jump back to the first page.
  useEffect(() => {
    setPage(0)
  }, [zone, filterType])

  // Group records by name and type - combine multiple IPs into single record
  const groupRecordsByNameAndType = (records: any[]) => {
    const grouped = new Map<string, any>()

    records.forEach(record => {
      const key = `${record.name}:${record.record_type}`

      if (grouped.has(key)) {
        // Add this IP to the existing record's values array
        const existing = grouped.get(key)
        if (!existing.values) {
          existing.values = [existing.value]
        }
        existing.values.push(record.value)
      } else {
        // First occurrence - initialize with single value
        grouped.set(key, {
          ...record,
          values: [record.value]
        })
      }
    })

    return Array.from(grouped.values())
  }

  const allRecords = data?.records ? groupRecordsByNameAndType(data.records) : []
  const totalPages = Math.max(1, Math.ceil(allRecords.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages - 1)
  const pageRecords = allRecords.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE,
  )

  const createMutation = useMutation({
    mutationFn: createDNSRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-records'] })
      setCreateDialogOpen(false)
      setUseMultipleIPs(false)
      setIpValues([''])
      toast.success('DNS record created successfully')
    },
    onError: (error) => {
      toast.error(`Failed to create record: ${error.message}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ zone, recordType, name, update }: {
      zone: string
      recordType: string
      name: string
      update: DNSRecordUpdate
    }) => updateDNSRecord(zone, recordType, name, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-records'] })
      setEditDialogOpen(false)
      setEditUseMultipleIPs(false)
      setEditIpValues([''])
      toast.success('DNS record updated successfully')
    },
    onError: (error) => {
      toast.error(`Failed to update record: ${error.message}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({ zone, recordType, name }: { zone: string; recordType: string; name: string }) =>
      deleteDNSRecord(zone, recordType, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-records'] })
      toast.success('DNS record deleted successfully')
    },
    onError: (error) => {
      toast.error(`Failed to delete record: ${error.message}`)
    },
  })

  const handleTestDNS = async (record: any) => {
    setResolvingRecord(record.record_id)
    try {
      const recordType = ['A', 'AAAA', 'CNAME', 'TXT', 'MX'].includes(record.record_type)
        ? record.record_type
        : 'A'
      const result = await resolveDNS(record.name, recordType)
      setResolveResult(result)
      setResolveDialogOpen(true)
    } catch (error) {
      toast.error(`DNS resolution failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setResolvingRecord(null)
    }
  }

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const name = (formData.get('name') as string).trim()

    // Validate customer restrictions
    if (!isAdmin) {
      // Customers can only create records in private zone
      if (zone !== 'private') {
        toast.error('You can only create records in the private zone')
        return
      }
      // Customers can only create aisep.* records
      if (!name.toLowerCase().startsWith('aisep.')) {
        toast.error('Record name must start with "aisep."')
        return
      }
    }

    // Build record - handle both single and multiple IPs
    let record: DNSRecordCreate

    if (useMultipleIPs) {
      // Filter out empty values and trim
      const filteredValues = ipValues
        .map(v => v.trim())
        .filter(v => v.length > 0)

      if (filteredValues.length === 0) {
        toast.error('Please provide at least one IP address')
        return
      }

      record = {
        name,
        record_type: formData.get('record_type') as string,
        value: filteredValues[0], // Required by API, but values takes precedence
        values: filteredValues,
        ttl: parseInt(formData.get('ttl') as string) || 300,
        zone,
        cloud_customer_id: (formData.get('cloud_customer_id') as string)?.trim(),
      }
    } else {
      record = {
        name,
        record_type: formData.get('record_type') as string,
        value: formData.get('value') as string,
        ttl: parseInt(formData.get('ttl') as string) || 300,
        zone,
        cloud_customer_id: (formData.get('cloud_customer_id') as string)?.trim(),
      }
    }

    createMutation.mutate(record)
  }

  const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedRecord) return
    const formData = new FormData(e.currentTarget)

    let update: DNSRecordUpdate

    if (editUseMultipleIPs) {
      // Filter out empty values and trim
      const filteredValues = editIpValues
        .map(v => v.trim())
        .filter(v => v.length > 0)

      if (filteredValues.length === 0) {
        toast.error('Please provide at least one IP address')
        return
      }

      update = {
        values: filteredValues,
        ttl: parseInt(formData.get('ttl') as string),
      }
    } else {
      update = {
        value: formData.get('value') as string,
        ttl: parseInt(formData.get('ttl') as string),
      }
    }

    updateMutation.mutate({
      zone: selectedRecord.zone,
      recordType: selectedRecord.record_type,
      name: selectedRecord.name,
      update,
    })
  }

  const handleDelete = (record: any) => {
    if (confirm(`Delete ${record.name} (${record.record_type})?`)) {
      deleteMutation.mutate({
        zone: record.zone,
        recordType: record.record_type,
        name: record.name,
      })
    }
  }

  // The zone's own SOA record and its apex NS records are managed by Route53
  // and cannot be deleted — hide the delete action for them. Subdomain NS
  // records (delegations, name ≠ zone apex) remain deletable.
  const isProtectedRecord = (record: any): boolean => {
    const type = String(record.record_type ?? '').toUpperCase()
    const name = String(record.name ?? '').replace(/\.$/, '').toLowerCase()
    const apex = String(record.zone_name ?? '').replace(/\.$/, '').toLowerCase()
    if (type === 'SOA') return true
    if (type === 'NS' && name === apex) return true
    return false
  }

  // Check if a customer user can edit/delete a record.
  // Customers can only modify aisep.*.{cloud_customer_id}.private records.
  // Admins can modify any record.
  const canEditRecord = (record: any): boolean => {
    // Admins have full access
    if (isAdmin) return true

    // Customers can only edit their own aisep.* records in private zone
    if (!cloudCustomerId) return false

    const name = String(record.name ?? '').replace(/\.$/, '').toLowerCase()
    const recordZone = String(record.zone ?? '').toLowerCase()

    // Must be in private zone
    if (recordZone !== 'private') return false

    // Must match pattern: aisep.*.{cloud_customer_id}.private
    // The full name includes the zone, so we need to check:
    // aisep.{anything}.{cloud_customer_id}.{zone_domain}
    const expectedPattern = `aisep.`
    const expectedOwner = `.${cloudCustomerId.toLowerCase()}.`

    return name.startsWith(expectedPattern) && name.includes(expectedOwner)
  }

  return (
    <AppShell title="DNS Records">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-2">
            <Button
              variant={zone === 'public' ? 'default' : 'outline'}
              onClick={() => setZone('public')}
            >
              {ZONE_LABELS.public}
            </Button>
            <Button
              variant={zone === 'private' ? 'default' : 'outline'}
              onClick={() => setZone('private')}
            >
              {ZONE_LABELS.private}
            </Button>
          </div>
          <div className="flex gap-2">
            <select
              className="h-10 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {RECORD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Record
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{ZONE_LABELS[zone]} - Records ({data?.total || 0})</CardTitle>
            <CardDescription>
              Manage DNS records for the {ZONE_LABELS[zone]}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : allRecords.length > 0 ? (
              <>
                {/* Fixed-height, scrollable list — keeps the card a stable size
                    regardless of how many records the current page holds. */}
                <div className="h-[560px] overflow-y-auto pr-1 space-y-2">
                  {pageRecords.map((record) => (
                    <div
                      key={record.record_id}
                      className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)] hover:border-[var(--primary)] transition-colors"
                    >
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[var(--fg)]">{record.name}</span>
                          <Badge variant="default">{record.record_type}</Badge>
                          <Badge variant="success">{record.status}</Badge>
                          {record.cloud_customer_id && (
                            <Badge variant="warning">owner: {record.cloud_customer_id}</Badge>
                          )}
                          {/* Show multi-IP badge */}
                          {record.values && record.values.length > 1 && (
                            <Badge variant="default">{record.values.length} IPs</Badge>
                          )}
                        </div>
                        {/* Display all IPs for this record */}
                        {record.values && record.values.length > 1 ? (
                          <div className="space-y-1">
                            <p className="text-xs text-[var(--fg-muted)] font-medium">
                              Load balanced across {record.values.length} IPs:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {record.values.map((ip: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="text-sm text-[var(--fg-muted)] px-2 py-1 rounded bg-[var(--bg-muted)] font-mono"
                                >
                                  {ip}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-[var(--fg-muted)]">{record.value}</p>
                        )}
                        <p className="text-xs text-[var(--fg-muted)]">TTL: {record.ttl}s</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTestDNS(record)}
                          disabled={resolvingRecord === record.record_id}
                          title="Test DNS resolution"
                        >
                          <Activity className="h-4 w-4" />
                        </Button>
                        {canEditRecord(record) && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record)
                                // Check if record has multiple IPs
                                if (record.values && record.values.length > 1) {
                                  setEditUseMultipleIPs(true)
                                  setEditIpValues(record.values)
                                } else {
                                  setEditUseMultipleIPs(false)
                                  setEditIpValues([record.value])
                                }
                                setEditDialogOpen(true)
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {!isProtectedRecord(record) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(record)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination footer */}
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border)]">
                  <span className="text-sm text-[var(--fg-muted)]">
                    Showing {currentPage * PAGE_SIZE + 1}–
                    {Math.min((currentPage + 1) * PAGE_SIZE, allRecords.length)} of{' '}
                    {allRecords.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Prev
                    </Button>
                    <span className="text-sm text-[var(--fg-muted)] tabular-nums">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-[var(--fg-muted)]">
                No DNS records found. Create your first record to get started.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onOpenChange={(open) => {
            setCreateDialogOpen(open)
            if (!open) {
              // Reset state when closing
              setUseMultipleIPs(false)
              setIpValues([''])
            }
          }}
        >
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create DNS Record</DialogTitle>
                <DialogDescription>
                  Add a new DNS record to the {ZONE_LABELS[zone]}
                  {!isAdmin && zone === 'private' && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400">
                      ⚠️ You can only create records starting with "aisep." in the private zone
                    </span>
                  )}
                  {!isAdmin && zone === 'public' && (
                    <span className="block mt-2 text-red-600 dark:text-red-400">
                      ⚠️ Customers cannot create records in the public zone
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    name="name"
                    placeholder={isAdmin ? "aisep.TDAISCUSTDV01" : "aisep.myapp"}
                    defaultValue={!isAdmin ? "aisep." : ""}
                    required
                  />
                  <p className="text-xs text-[var(--muted-fg)] mt-1">
                    {!isAdmin ? (
                      <>
                        Must start with <code>aisep.</code> — Final record:{' '}
                        <code>{`aisep.{your-name}.${cloudCustomerId ?? 'your-id'}.${zone === 'private' ? allRecords[0]?.zone_name ?? 'private domain' : 'public domain'}`}</code>
                      </>
                    ) : (
                      <>
                        Final record:{' '}
                        <code>{`{name}.{cloud_customer_id}.${allRecords[0]?.zone_name ?? zone + ' domain'}`}</code>
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Cloud Customer ID</label>
                  <Input
                    // Remount when identity resolves so the prefill applies even
                    // if /me loaded after the dialog first rendered.
                    key={cloudCustomerId ?? 'anon'}
                    name="cloud_customer_id"
                    placeholder="33567"
                    // Admins choose which customer owns the record; a customer's
                    // own id is prefilled and locked so records stay in-tenant.
                    defaultValue={isAdmin ? '' : cloudCustomerId ?? ''}
                    readOnly={!isAdmin && !!cloudCustomerId}
                    required
                  />
                  <p className="text-xs text-[var(--muted-fg)] mt-1">
                    Owning tenant — embedded into the record name; determines who can view/edit it.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select
                    name="record_type"
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--bg)] px-3 text-sm"
                    required
                  >
                    {RECORD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Value(s)</label>
                    <label className="flex items-center gap-2 text-sm text-[var(--fg-muted)]">
                      <input
                        type="checkbox"
                        checked={useMultipleIPs}
                        onChange={(e) => {
                          setUseMultipleIPs(e.target.checked)
                          if (e.target.checked && ipValues.length === 1 && !ipValues[0]) {
                            setIpValues(['', ''])
                          }
                        }}
                        className="rounded border-[var(--border)]"
                      />
                      Multiple IPs (Load Balancing)
                    </label>
                  </div>

                  {useMultipleIPs ? (
                    <div className="space-y-2">
                      <p className="text-xs text-[var(--muted-fg)]">
                        Add multiple IP addresses for load balancing and high availability
                      </p>
                      {ipValues.map((value, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={value}
                            onChange={(e) => {
                              const newValues = [...ipValues]
                              newValues[index] = e.target.value
                              setIpValues(newValues)
                            }}
                            placeholder={`IP ${index + 1} (e.g., 10.0.1.10${index})`}
                          />
                          {ipValues.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newValues = ipValues.filter((_, i) => i !== index)
                                setIpValues(newValues.length > 0 ? newValues : [''])
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIpValues([...ipValues, ''])}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add IP Address
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input name="value" placeholder="10.0.1.100" required />
                      <p className="text-xs text-[var(--muted-fg)] mt-1">
                        Single IP address or value
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">TTL (seconds)</label>
                  <Input name="ttl" type="number" defaultValue="300" required />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Spinner size="sm" /> : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open)
            if (!open) {
              // Reset state when closing
              setEditUseMultipleIPs(false)
              setEditIpValues([''])
            }
          }}
        >
          <DialogContent>
            <form onSubmit={handleUpdate}>
              <DialogHeader>
                <DialogTitle>Edit DNS Record</DialogTitle>
                <DialogDescription>
                  Update {selectedRecord?.name} ({selectedRecord?.record_type})
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Value(s)</label>
                    <label className="flex items-center gap-2 text-sm text-[var(--fg-muted)]">
                      <input
                        type="checkbox"
                        checked={editUseMultipleIPs}
                        onChange={(e) => {
                          setEditUseMultipleIPs(e.target.checked)
                          if (e.target.checked && editIpValues.length === 1) {
                            // Switching to multi-IP - add one more field
                            setEditIpValues([...editIpValues, ''])
                          }
                        }}
                        className="rounded border-[var(--border)]"
                      />
                      Multiple IPs (Load Balancing)
                    </label>
                  </div>

                  {editUseMultipleIPs ? (
                    <div className="space-y-2">
                      <p className="text-xs text-[var(--muted-fg)]">
                        Edit, add, or remove IP addresses for load balancing
                      </p>
                      {editIpValues.map((value, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={value}
                            onChange={(e) => {
                              const newValues = [...editIpValues]
                              newValues[index] = e.target.value
                              setEditIpValues(newValues)
                            }}
                            placeholder={`IP ${index + 1} (e.g., 10.0.1.10${index})`}
                          />
                          {editIpValues.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newValues = editIpValues.filter((_, i) => i !== index)
                                setEditIpValues(newValues.length > 0 ? newValues : [''])
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditIpValues([...editIpValues, ''])}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add IP Address
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input name="value" defaultValue={selectedRecord?.value} required />
                      <p className="text-xs text-[var(--muted-fg)] mt-1">
                        Single IP address or value
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">TTL (seconds)</label>
                  <Input name="ttl" type="number" defaultValue={selectedRecord?.ttl} required />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? <Spinner size="sm" /> : 'Update'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* DNS Resolution Test Dialog */}
        <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>DNS Resolution Test</DialogTitle>
              <DialogDescription>
                Testing DNS resolution for {resolveResult?.hostname}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-4">
              {resolveResult && (
                <>
                  <div className="p-4 rounded-lg border border-[var(--border)]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">Hostname:</span>
                      <span className="text-sm text-[var(--fg)]">{resolveResult.hostname}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">Record Type:</span>
                      <Badge variant="default">{resolveResult.record_type}</Badge>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">Status:</span>
                      <Badge variant={resolveResult.resolved ? 'success' : 'error'}>
                        {resolveResult.resolved ? 'Resolved' : 'Failed'}
                      </Badge>
                    </div>
                  </div>

                  {resolveResult.resolved && resolveResult.records.length > 0 && (
                    <div>
                      <label className="text-sm font-medium block mb-2">Resolved Records:</label>
                      <div className="space-y-2">
                        {resolveResult.records.map((record, idx) => (
                          <div
                            key={idx}
                            className="p-3 rounded-md bg-[var(--bg-muted)] text-sm font-mono"
                          >
                            {record}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!resolveResult.resolved && resolveResult.error && (
                    <div className="p-4 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20">
                      <span className="text-sm font-medium text-red-700 dark:text-red-400">
                        Error:
                      </span>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                        {resolveResult.error}
                      </p>
                    </div>
                  )}

                  <div className="text-xs text-[var(--fg-muted)] italic">
                    This test performs actual DNS queries using the server's DNS resolver,
                    without relying on HTTP/HTTPS protocols. It's useful for testing DNS records
                    that don't have web servers or certificates configured.
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setResolveDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  )
}
